import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Copy, Check, Smile, Frown, Meh, AlertCircle, CheckCircle2, Target, TrendingUp, Loader2, Sparkles } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { insightsService } from "@/services/insightsService";
import { wellnessService } from "@/services/wellnessService";
import { ReflectionAnalysis, ActionSuggestion } from "@/types/user";
import ActionSuggestions from "@/components/ActionSuggestions";
import MoodSelector from "@/components/MoodSelector";

interface VoiceCheckInSyncResult {
  voiceCheckInId: string;
  transcript: string;
  confidence: number;
  checkInId?: string | null;
  status: 'completed';
  mood: number;
  energy: number;
  focus: number;
  motivation: number;
  tasks: string[];
  blockers: string | null;
  wins: string | null;
  helpNeeded: boolean;
  helpWith: string | null;
  semanticAnalysis: {
    sentiment?: 'positive' | 'neutral' | 'negative';
    urgency?: 'high' | 'medium' | 'low';
    skills?: string[];
    topics?: string[];
    temporalMarkers?: string[];
  } | null;
}

interface VoiceCheckInResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: VoiceCheckInSyncResult;
}

export default function VoiceCheckInResultsModal({
  isOpen,
  onClose,
  results,
}: VoiceCheckInResultsModalProps) {
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'results' | 'reflection'>('results');
  const [reflectionAnalysis, setReflectionAnalysis] = useState<ReflectionAnalysis | null>(null);
  const [actionSuggestions, setActionSuggestions] = useState<ActionSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editableMood, setEditableMood] = useState(results.mood);
  const [editableEnergy, setEditableEnergy] = useState(results.energy);
  const [editableFocus, setEditableFocus] = useState(results.focus);
  const [editableMotivation, setEditableMotivation] = useState(results.motivation);
  const [currentResults, setCurrentResults] = useState<VoiceCheckInSyncResult>(results);
  const { toast } = useToast();

  const handleCopyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(results.transcript);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Transcript copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy transcript to clipboard",
        variant: "destructive",
      });
    }
  };

  const getMoodEmoji = (mood: number) => {
    if (mood >= 4) return "😊";
    if (mood >= 3) return "🙂";
    if (mood >= 2) return "😐";
    return "😞";
  };

  const getMoodLabel = (mood: number) => {
    if (mood >= 4) return "Great";
    if (mood >= 3) return "Good";
    if (mood >= 2) return "Okay";
    return "Low";
  };

  const getSentimentBadgeVariant = (sentiment?: string) => {
    if (sentiment === 'positive') return 'default';
    if (sentiment === 'negative') return 'destructive';
    return 'secondary';
  };

  const getUrgencyBadgeVariant = (urgency?: string) => {
    if (urgency === 'high') return 'destructive';
    if (urgency === 'medium') return 'default';
    return 'secondary';
  };

  const fetchReflectionAndSuggestions = async (mood: number, energy: number, focus: number, motivation: number) => {
    setIsLoading(true);
    try {
      // Fetch reflection analysis
      const analysis = await insightsService.analyzeReflection({
        reflection: currentResults.transcript,
        moodScore: mood,
        energyLevel: energy,
        focusLevel: focus,
      });
      setReflectionAnalysis(analysis);

      // Fetch action suggestions
      const checkInData = {
        mood,
        energy,
        focus,
        motivation,
        tasks: currentResults.tasks,
        wins: currentResults.wins,
        blockers: currentResults.blockers,
        reflection: currentResults.transcript,
        helpNeeded: currentResults.helpNeeded,
        helpWith: currentResults.helpWith,
      };

      const suggestions = await insightsService.generateSuggestions({
        currentCheckIn: checkInData,
        limit: 5,
      });

      const suggestionsArray = Array.isArray(suggestions) ? suggestions : (suggestions?.suggestions || []);
      setActionSuggestions(suggestionsArray);
      setViewMode('reflection');
    } catch (error: any) {
      console.error("Error fetching reflection and suggestions:", error);
      toast({
        title: "Error",
        description: "Failed to load reflection analysis and suggestions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    // Use original values
    await fetchReflectionAndSuggestions(
      results.mood,
      results.energy,
      results.focus,
      results.motivation
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Update voice check-in with edited values
      await wellnessService.updateVoiceCheckIn(currentResults.voiceCheckInId, {
        mood: editableMood,
        energy: editableEnergy,
        focus: editableFocus,
        motivation: editableMotivation,
      });

      // Update local results state
      const updatedResults = {
        ...currentResults,
        mood: editableMood,
        energy: editableEnergy,
        focus: editableFocus,
        motivation: editableMotivation,
      };
      setCurrentResults(updatedResults);

      toast({
        title: "Saved!",
        description: "Voice check-in updated successfully",
      });

      // Fetch reflection and suggestions with updated values
      await fetchReflectionAndSuggestions(
        editableMood,
        editableEnergy,
        editableFocus,
        editableMotivation
      );
    } catch (error: any) {
      console.error("Error updating voice check-in:", error);
      toast({
        title: "Error",
        description: "Failed to update voice check-in. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleActionSelected = (action: ActionSuggestion) => {
    // Remove the selected action from the list
    setActionSuggestions((prev) => prev.filter((a) => a.id !== action.id));
  };

  const handleClose = () => {
    // Reset state when closing
    setViewMode('results');
    setReflectionAnalysis(null);
    setActionSuggestions([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Only allow closing from Done button in reflection view
      if (!open && viewMode === 'reflection') {
        handleClose();
      }
    }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col overflow-hidden [&>button]:hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {viewMode === 'results' ? 'Voice Check-in Results' : 'Reflection & Suggestions'}
          </DialogTitle>
          <DialogDescription>
            {viewMode === 'results' 
              ? 'Your voice check-in has been processed and analyzed'
              : 'Based on your voice check-in, here are personalized insights and recommendations'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-4">
            {viewMode === 'results' ? (
              <>
            {/* Transcript Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Transcript</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyTranscript}
                    className="h-8"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3 w-3 mr-2" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <CardDescription>
                  Confidence: {(results.confidence * 100).toFixed(1)}%
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {results.transcript}
                </p>
              </CardContent>
            </Card>

            {/* Metrics Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Metrics</CardTitle>
                <CardDescription>Adjust the extracted values if needed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mood */}
                <div className="space-y-2">
                  <Label className="text-base">How are you feeling today?</Label>
                  <MoodSelector 
                    value={editableMood} 
                    onChange={setEditableMood} 
                  />
                </div>

                {/* Energy */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label htmlFor="energy" className="text-base">Energy Level</Label>
                    <span className="text-sm text-muted-foreground">
                      {editableEnergy}%
                    </span>
                  </div>
                  <Slider
                    id="energy"
                    value={[editableEnergy]}
                    onValueChange={(v) => setEditableEnergy(v[0])}
                    max={100}
                    step={1}
                    className="py-2"
                    disabled={isLoading || isSaving}
                  />
                </div>

                {/* Focus */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label htmlFor="focus" className="text-base">Focus Level</Label>
                    <span className="text-sm text-muted-foreground">
                      {editableFocus}%
                    </span>
                  </div>
                  <Slider
                    id="focus"
                    value={[editableFocus]}
                    onValueChange={(v) => setEditableFocus(v[0])}
                    max={100}
                    step={1}
                    className="py-2"
                    disabled={isLoading || isSaving}
                  />
                </div>

                {/* Motivation */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label htmlFor="motivation" className="text-base">Motivation Level</Label>
                    <span className="text-sm text-muted-foreground">
                      {editableMotivation}%
                    </span>
                  </div>
                  <Slider
                    id="motivation"
                    value={[editableMotivation]}
                    onValueChange={(v) => setEditableMotivation(v[0])}
                    max={100}
                    step={1}
                    className="py-2"
                    disabled={isLoading || isSaving}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Tasks */}
            {results.tasks && results.tasks.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Tasks
                  </CardTitle>
                  <CardDescription>{results.tasks.length} task(s) mentioned</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {results.tasks.map((task, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-muted-foreground mt-1">•</span>
                        <span>{task}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Blockers */}
            {results.blockers && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-orange-600">
                    <AlertCircle className="h-4 w-4" />
                    Blockers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{results.blockers}</p>
                </CardContent>
              </Card>
            )}

            {/* Wins */}
            {results.wins && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    Wins
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{results.wins}</p>
                </CardContent>
              </Card>
            )}

            {/* Help Needed */}
            {results.helpNeeded && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-600">
                    <AlertCircle className="h-4 w-4" />
                    Help Needed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">
                    {results.helpWith || "Help requested"}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Semantic Analysis */}
            {results.semanticAnalysis && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {results.semanticAnalysis.sentiment && (
                    <div>
                      <span className="text-sm font-medium mr-2">Sentiment:</span>
                      <Badge variant={getSentimentBadgeVariant(results.semanticAnalysis.sentiment)}>
                        {results.semanticAnalysis.sentiment}
                      </Badge>
                    </div>
                  )}
                  
                  {results.semanticAnalysis.urgency && (
                    <div>
                      <span className="text-sm font-medium mr-2">Urgency:</span>
                      <Badge variant={getUrgencyBadgeVariant(results.semanticAnalysis.urgency)}>
                        {results.semanticAnalysis.urgency}
                      </Badge>
                    </div>
                  )}

                  {results.semanticAnalysis.skills && results.semanticAnalysis.skills.length > 0 && (
                    <div>
                      <span className="text-sm font-medium block mb-2">Skills:</span>
                      <div className="flex flex-wrap gap-2">
                        {results.semanticAnalysis.skills.map((skill, index) => (
                          <Badge key={index} variant="secondary">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {results.semanticAnalysis.topics && results.semanticAnalysis.topics.length > 0 && (
                    <div>
                      <span className="text-sm font-medium block mb-2">Topics:</span>
                      <div className="flex flex-wrap gap-2">
                        {results.semanticAnalysis.topics.map((topic, index) => (
                          <Badge key={index} variant="outline">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
              </>
            ) : (
              <>
                {/* Reflection Analysis */}
                {reflectionAnalysis && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Sparkles className="h-4 w-4" />
                        Reflection Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {reflectionAnalysis.insightSummary && (
                        <div>
                          <p className="text-sm leading-relaxed">{reflectionAnalysis.insightSummary}</p>
                        </div>
                      )}

                      {reflectionAnalysis.keyThemes && reflectionAnalysis.keyThemes.length > 0 && (
                        <div>
                          <span className="text-sm font-medium block mb-2">Key Themes:</span>
                          <div className="flex flex-wrap gap-2">
                            {reflectionAnalysis.keyThemes.map((theme, index) => (
                              <Badge key={index} variant="secondary">
                                {theme}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {reflectionAnalysis.focusAreas && reflectionAnalysis.focusAreas.length > 0 && (
                        <div>
                          <span className="text-sm font-medium block mb-2">Focus Areas:</span>
                          <ul className="space-y-2">
                            {reflectionAnalysis.focusAreas.map((area, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm">
                                <span className="text-muted-foreground mt-1">•</span>
                                <span>{area}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {reflectionAnalysis.suggestedActions && reflectionAnalysis.suggestedActions.length > 0 && (
                        <div>
                          <span className="text-sm font-medium block mb-2">Suggested Actions:</span>
                          <ul className="space-y-2">
                            {reflectionAnalysis.suggestedActions.map((action, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm">
                                <span className="text-muted-foreground mt-1">•</span>
                                <span>{action}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Action Suggestions */}
                {actionSuggestions.length > 0 && (
                  <div>
                    <ActionSuggestions
                      onActionSelected={handleActionSelected}
                      preGeneratedSuggestions={actionSuggestions}
                      checkInData={{
                        mood: currentResults.mood,
                        energy: currentResults.energy,
                        focus: currentResults.focus,
                        motivation: currentResults.motivation,
                        tasks: currentResults.tasks,
                        wins: currentResults.wins,
                        blockers: currentResults.blockers,
                        reflection: currentResults.transcript,
                        helpNeeded: currentResults.helpNeeded,
                        helpWith: currentResults.helpWith,
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t">
          {viewMode === 'results' ? (
            <div className="flex gap-2 w-full">
              <Button 
                onClick={handleSkip} 
                disabled={isLoading || isSaving}
                variant="outline"
                className="flex-1"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  'Skip'
                )}
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={isLoading || isSaving}
                className="flex-1"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving & Analyzing...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          ) : (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}